import { CreatorResponse, MultiProductResponse } from "@/@types/data/collections";
import { safeSessionStorage } from "@/utils/safeStorages";
import React from "react";
import siteConfig from "../variables";
import { useQueryParams } from "./useQueryParams";
import { GetRequest, useRequest } from "./useRequests";
import { useXSSOAuth } from "./useXSSOAuth";
import { processResponseWithSections } from "./utils";

const { AFFILIATE_PARAMETER_NAME, KEY_AFFILIATE } = siteConfig;

type GetProductsReqBody = {
  gameId: string;
  productId: string;
  productType: string;
}[];

export const useCreatorContent = (source?: string, useCache = true) => {
  const { isLoggedIn, login } = useXSSOAuth();
  const query = useQueryParams();
  const sessionStoreValue = safeSessionStorage.getItem(KEY_AFFILIATE);
  const trafficSource = source || query.get(AFFILIATE_PARAMETER_NAME) || (useCache && sessionStoreValue) || "";
  const haveAffiliate = trafficSource !== "";

  React.useEffect(() => {
    safeSessionStorage.setItem(KEY_AFFILIATE, trafficSource);
  }, [trafficSource]);

  // Initial state (empty values indicate no request)
  const [contentRequest, setContentRequest] = React.useState<GetRequest>({} as GetRequest);
  const [authenticatedContentRequest, setAuthenticatedContentRequest] = React.useState<GetRequest>({} as GetRequest);

  // Initial request states
  // TODO: HermesError handling
  const { isValidating: isValidatingContents, data: creatorContentsResp } =
    useRequest<MultiProductResponse>(contentRequest);
  const {
    isValidating: isValidatingAuthedContents,
    data: authCreatorContentsResp,
    ...rest
  } = useRequest<MultiProductResponse>(authenticatedContentRequest);

  const isValidating = isValidatingContents || isValidatingAuthedContents;
  // Update if we're logged on, or if we have an affiliate
  React.useEffect(() => {
    if (haveAffiliate) {
      setContentRequest({
        url: `/v3/creators/${trafficSource}/content`,
      });
    }
  }, [haveAffiliate]);

  const getProductRequestBody: GetProductsReqBody | undefined = React.useMemo(
    () =>
      // Parse the collectionContent data and create a request body for getting collectionContentAuth data
      creatorContentsResp?.products?.map(({ game, id, type }) => ({
        gameId: game,
        productId: id,
        productType: type,
      })),
    [creatorContentsResp?.products],
  );

  React.useEffect(() => {
    if (isLoggedIn && getProductRequestBody !== undefined) {
      setAuthenticatedContentRequest({
        url: `/v2/get-product-quotes`,
        method: "POST",
        data: getProductRequestBody,
      });
    }
  }, [isLoggedIn, getProductRequestBody]);

  if (rest.response?.status === 401) {
    login();
    return {
      trafficSource,
      isValidating: isValidating,
      ...processResponseWithSections(creatorContentsResp, authCreatorContentsResp),
    };
  }
  return {
    trafficSource,
    isValidating: isValidating,
    ...processResponseWithSections(creatorContentsResp, authCreatorContentsResp),
  };
};

/**
 * useAffiliate will try to get the affiliate from the url, if that doesn't exist, then it would grab it from sessionStorage.
 * When we unmount it will save the current affilate to the sessionStorage for reuse.
 *
 * @returns {object} affiliate of the session.
 */
export const useCreator = (source?: string, useSavedData = true) => {
  const query = useQueryParams();
  const sessionStoreValue = safeSessionStorage.getItem(KEY_AFFILIATE);
  const trafficSource = source || query.get(AFFILIATE_PARAMETER_NAME) || (useSavedData && sessionStoreValue) || "";
  const haveAffiliate = trafficSource !== "";

  React.useEffect(() => {
    safeSessionStorage.setItem(KEY_AFFILIATE, trafficSource);
  }, [trafficSource]);

  // Initial state (empty values indicate no request)
  const [affiliateRequest, setAffiliateRequest] = React.useState<GetRequest>({} as GetRequest);

  // Initial request states
  const { isValidating, data: creatorResp } = useRequest<CreatorResponse>(affiliateRequest);

  // Update if we're logged on, or if we have an affiliate
  React.useEffect(() => {
    if (haveAffiliate) {
      setAffiliateRequest({
        url: `/v3/creators/${trafficSource}`,
      });
    }
  }, [haveAffiliate]);

  return {
    trafficSource,
    isValidating: isValidating,
    ...creatorResp,
  };
};
